/**
 * DiligenceGPT™ site configuration: every outbound destination lives here.
 *
 * LINKS TO CONFIRM BEFORE PUBLISHING
 * - GET_STARTED_URL: an earlier brief supplied "app.dpgt.io", which looks like a
 *   transposition. This project previously used "https://app.dgpt.io/get-started"
 *   (StartupFuel site), so that hostname is used here. Confirm it resolves.
 * - SIGN_IN_URL: assumed app root. Confirm the real sign-in path.
 * - PRIVACY_URL / TERMS_URL: unverified paths on www.dgpt.io. Confirm they exist.
 */
export const GET_STARTED_URL = 'https://app.dgpt.io/get-started'
export const SIGN_IN_URL = 'https://app.dgpt.io'
export const ENTERPRISE_DEMO_URL = 'https://www.calendly.com/mrmartis'
/** In-site sample memo page. */
export const SAMPLE_MEMO_PATH = '/sample-memo'
/**
 * Downloadable sample memo (PDF). Leave empty until a real, approved file is hosted —
 * the download button is hidden while this is empty.
 */
export const SAMPLE_MEMO_DOWNLOAD_URL = ''
export const PRIVACY_URL = 'https://www.dgpt.io/privacy'
export const TERMS_URL = 'https://www.dgpt.io/terms'
export const STARTUPFUEL_URL = 'https://www.startupfuel.com'

/** Canonical marketing origin used for canonical URLs, sitemap and structured data. */
export const SITE_ORIGIN = 'https://dgpt.io'

/** Absolute URL of a 1200×630 social-sharing image. Leave empty until a real asset is hosted. */
export const OG_IMAGE_URL = ''

export const BRAND = {
  product: 'DiligenceGPT™',
  /** Secondary brand line — used at most once on the homepage. */
  tagline: 'Diligence at the Speed of Dealflow.',
} as const

export const VIDEO = {
  id: '1238409079',
  title: 'DiligenceGPT™: Venture Capital',
  embedUrl:
    'https://player.vimeo.com/video/1238409079?badge=0&autopause=0&player_id=0&app_id=58479',
  playerApi: 'https://player.vimeo.com/api/player.js',
  oembedUrl: 'https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2F1238409079&width=1920',
  posterLocal: '' as string,
  posterWidth: 1920,
  posterHeight: 1080,
} as const

export const NAV_LINKS = [
  { label: 'Tools', href: '/tools' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Sample Memo', href: SAMPLE_MEMO_PATH },
  { label: 'About', href: '/about' },
] as const

export const FOOTER_LINKS = [
  { label: 'Tools', href: '/tools' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Sample Memo', href: SAMPLE_MEMO_PATH },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Privacy', href: PRIVACY_URL },
  { label: 'Terms', href: TERMS_URL },
  { label: 'StartupFuel', href: STARTUPFUEL_URL },
] as const

/** Organizations shown on dgpt.io (approved logo files). */
export const ECOSYSTEM_LOGOS = [
  { name: 'Techstars', src: 'https://cdn.magicpatterns.com/uploads/ic2swpBvjg2QntGFRw2efH/64660d70c2eb8d2664ed7518_Techstars_Logo_Primary_(2).png', scale: 1.35 },
  { name: 'The World Bank', src: 'https://cdn.magicpatterns.com/uploads/h4TWuv6FSmSQMxCzj28ZV8/world-bank-logo_(1).png', scale: 1.9 },
  { name: 'Village Capital', src: 'https://cdn.magicpatterns.com/uploads/7XE4WsCbnnYMJsWUCZ3P5B/Website_Village_Capital_Logo_(2).png', scale: 1.15 },
  { name: 'Blue Vision Capital', src: 'https://cdn.magicpatterns.com/uploads/pHSn8xj5AwkNYeXsZKuXoT/screenshot-2022-12-22-at-10-23-55-am_(2).png', scale: 2.1 },
  { name: 'Measured Capital', src: 'https://cdn.magicpatterns.com/uploads/xfZnhsmVckaAoMyy5uu4Vp/Asset2B1_(2).webp', scale: 0.95 },
  { name: 'IBM', src: 'https://cdn.magicpatterns.com/uploads/2TNBFD5JzP9FgnfGE4rfjN/ibm-logo_(2).webp', scale: 0.65 },
  { name: 'Sony', src: 'https://cdn.magicpatterns.com/uploads/5m2cCQ6UnAKCWQZzTuBbji/sony-logo_(1).svg', scale: 0.8 },
] as const

export const DGPT_LOGO_SRC =
  'https://cdn.magicpatterns.com/uploads/izXwPzD6vEjAVuwXw1X7o8/dgptlogo-DugKi5EU.png'

/** Real product screenshots supplied for the site. */
export const PRODUCT_SCREENSHOTS = {
  memo: {
    src: 'https://cdn.magicpatterns.com/uploads/u8cBCRgeaDagKdr5XGZqzs/Screenshot_2026-10-21_at_1.05.34_PM.png',
    alt: 'DiligenceGPT™ Deal Memo and Business Risk report cards for a company',
    width: 1598,
    height: 1174,
  },
  pipeline: {
    src: 'https://cdn.magicpatterns.com/uploads/5aZEJcNKtvoNdzz35A2Mi6/Screenshot_2026-10-21_at_1.06.06_PM.png',
    alt: 'DiligenceGPT™ pipeline table showing a fit score for each deal against the fund’s thesis',
    width: 2360,
    height: 1440,
    crop: { aspect: '2360 / 860', position: 'bottom' },
  },
  thesis: {
    src: 'https://cdn.magicpatterns.com/uploads/qcyJyPGrRHtCKtC3TBXvxJ/Screenshot_2026-10-21_at_1.19.45_PM.png',
    alt: 'DiligenceGPT™ investment thesis settings where a fund defines its criteria',
    width: 2436,
    height: 1506,
    crop: { aspect: '2436 / 790', position: 'bottom' },
  },
} as const

/**
 * Customer evidence from testimonials published on dgpt.io. No verified metrics
 * exist, so `result` stays null and is not rendered. Relationship types preserved.
 */
export const CUSTOMER_EVIDENCE: {
  organization: string
  relationship: string
  useCase: string
  work: string
  result: string | null
  resultBasis: string | null
  quote: string
  person: string
  role: string
}[] = [
  {
    organization: 'Build In Tulsa',
    relationship: 'Accelerator program',
    useCase: 'Reviewing startups applying to the program.',
    work: 'Standardized ratings and company assessments for applicants.',
    result: null,
    resultBasis: null,
    quote:
      'Build In Tulsa used DiligenceGPT to generate standardized, unbiased ratings with company assessments, which helped them understand their investability. They were able to filter, review, and provide feedback to the startups entering their program.',
    person: 'Montana',
    role: 'Program Director',
  },
  {
    organization: 'Blue Vision Capital',
    relationship: 'Venture fund',
    useCase: 'Filtering inbound deal flow at the top of the funnel.',
    work: 'Diligence support for early-stage screening.',
    result: null,
    resultBasis: null,
    quote:
      'The diligence service provided is so enriching, having a support team to help filter startup deal flow at the top of the funnel helps increase what we can see.',
    person: 'Meir Rabkin',
    role: 'GP',
  },
  {
    organization: 'Rogers Accelerator',
    relationship: 'Accelerator',
    useCase: 'Comparing internal diligence with an independent view.',
    work: 'Third-party diligence on companies the team was assessing.',
    result: null,
    resultBasis: null,
    quote:
      'The SF diligence gives us a 3rd party, unbiased way to compare our team’s work with experts. Their process is thorough for diligence.',
    person: 'Sumit',
    role: 'Director',
  },
]

export const PILOT_NOTE = 'Pilot in progress with Sony Innovation Fund.'

/** Security statements as currently published on dgpt.io — do not upgrade wording. */
export const PUBLISHED_SECURITY = [
  'Customer deal data is not used to train our models or third-party models.',
  'Workspaces and funds are tenant-isolated.',
  'SOC 2 audit is in progress (not yet certified).',
] as const

export const FOUNDER = {
  name: 'Ashley Rahul Martis',
  role: 'Founder & CEO, StartupFuel',
  /** Real photo URL when supplied. Empty = no photo (never a generated person). */
  photo: '',
  /** Verified biography. Expand only with approved material. */
  bio: 'Ashley leads StartupFuel, the company behind DiligenceGPT™. StartupFuel works with venture funds, accelerators and startup programs on screening and diligence.',
} as const

export const HOME_SEO = {
  title: 'DiligenceGPT™ | AI Due Diligence for VC Teams',
  description:
    'AI due diligence for venture capital teams. Screen pitch decks against your investment thesis, review data rooms, and prepare investment memos for analyst review.',
  keywords: [
    'AI due diligence for venture capital',
    'AI investment memo generator',
    'AI pitch deck screening',
    'startup due diligence software',
    'investment thesis screening',
    'AI data room analysis',
  ],
} as const
